import { useRef, useState, useEffect } from 'react';
import {
  TrendUp,
  TrendDown,
  Wallet,
  Truck,
  Users,
  Wrench,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  MilesStructuredDataBlock,
  MilesMetricCard,
  MilesLineChartData,
  MilesBarChartData,
  MilesPieChartData,
  MilesTableData,
  MilesComparisonTableData,
  MilesForecastChartData,
  MilesInsightsListData,
} from '../../types/apiTypes';
import {
  Warning,
  TrendUp as TrendUpIcon,
  TrendDown as TrendDownIcon,
  Lightbulb,
  ShieldWarning,
  ChartLine,
  ArrowRight,
} from '@phosphor-icons/react';

interface MilesStructuredDataProps {
  block: MilesStructuredDataBlock;
}

/**
 * Renders structured data blocks (metric cards, tables, charts) from MILES responses.
 */
function MilesStructuredData({ block }: MilesStructuredDataProps) {
  switch (block.type) {
    case 'metric_cards':
      return (
        <MetricCardsBlock
          title={block.title}
          data={block.data as MilesMetricCard[]}
        />
      );
    case 'table':
      return (
        <TableBlock title={block.title} data={block.data as MilesTableData} />
      );
    case 'comparison_table':
      return (
        <ComparisonTableBlock
          title={block.title}
          data={block.data as MilesComparisonTableData}
        />
      );
    case 'pie_chart':
      return (
        <PieChartBlock
          title={block.title}
          data={block.data as MilesPieChartData}
        />
      );
    case 'line_chart':
      return (
        <LineChartBlock
          title={block.title}
          data={block.data as MilesLineChartData}
        />
      );
    case 'bar_chart':
      return (
        <BarChartBlock
          title={block.title}
          data={block.data as MilesBarChartData}
        />
      );
    case 'forecast_chart':
      return (
        <ForecastChartBlock
          title={block.title}
          data={block.data as MilesForecastChartData}
        />
      );
    case 'insights_list':
      return (
        <InsightsListBlock
          title={block.title}
          data={block.data as MilesInsightsListData}
        />
      );
    default:
      return null;
  }
}

// ============================================================================
// Metric Cards
// ============================================================================

interface MetricCardsBlockProps {
  title?: string;
  data: MilesMetricCard[];
}

function MetricCardsBlock({ title, data }: MetricCardsBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  // Use ResizeObserver to detect container width and switch layouts
  // In collapsed chat view (~400px), cards need vertical stacking
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Switch to compact/vertical layout when container is narrow (collapsed chat)
        setIsCompact(entry.contentRect.width < 450);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Guard against empty data
  if (!data || data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="rounded-xl border border-gray-200 bg-white p-4"
    >
      {title && (
        <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
      )}
      <div
        className={
          isCompact
            ? 'grid grid-cols-3 gap-2'
            : 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
        }
      >
        {data.map((card, index) => (
          <MetricCard key={index} card={card} isCompact={isCompact} />
        ))}
      </div>
    </div>
  );
}

interface MetricCardProps {
  card: MilesMetricCard;
  isCompact?: boolean;
}

function MetricCard({ card, isCompact = false }: MetricCardProps) {
  const IconComponent = getIconComponent(card.icon);
  const changeColor =
    card.change && card.change > 0 ? 'text-green-600' : 'text-red-600';

  // Compact/vertical layout for collapsed chat view
  if (isCompact) {
    return (
      <div className="flex flex-col items-center rounded-lg bg-gray-50 px-2 py-3 text-center">
        {IconComponent && (
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <IconComponent size={18} className="text-primary" weight="fill" />
          </div>
        )}
        <p className="text-lg font-semibold leading-tight text-gray-900">
          {card.value}
        </p>
        <p className="mt-0.5 text-[10px] leading-tight text-gray-500">
          {card.label}
        </p>
        {card.change !== undefined && (
          <p className={`mt-0.5 text-[10px] ${changeColor}`}>
            {card.change > 0 ? '+' : ''}
            {card.change}%
          </p>
        )}
      </div>
    );
  }

  // Standard horizontal layout for expanded/fullscreen view
  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
      {IconComponent && (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <IconComponent size={20} className="text-primary" weight="fill" />
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        <p className="truncate text-xs text-gray-500">{card.label}</p>
        <p className="truncate text-lg font-semibold text-gray-900">
          {card.value}
        </p>
        {card.change !== undefined && (
          <p className={`text-xs ${changeColor}`}>
            {card.change > 0 ? '+' : ''}
            {card.change}%{card.changeLabel && ` ${card.changeLabel}`}
          </p>
        )}
      </div>
    </div>
  );
}

function getIconComponent(iconName?: string) {
  if (!iconName) return null;

  const iconMap: Record<string, typeof Wallet> = {
    Wallet: Wallet,
    TrendUp: TrendUp,
    TrendDown: TrendDown,
    Truck: Truck,
    Users: Users,
    Wrench: Wrench,
    CheckCircle: CheckCircle,
    XCircle: XCircle,
  };

  return iconMap[iconName] || null;
}

// ============================================================================
// Table
// ============================================================================

interface TableBlockProps {
  title?: string;
  data: MilesTableData;
}

function TableBlock({ title, data }: TableBlockProps) {
  // Guard against empty data
  if (!data.headers.length || !data.rows.length) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {title && (
        <div className="border-b border-gray-200 px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {data.headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left font-medium text-gray-600"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-4 py-2 text-gray-900">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Comparison Table
// ============================================================================

interface ComparisonTableBlockProps {
  title?: string;
  data: MilesComparisonTableData;
}

function ComparisonTableBlock({ title, data }: ComparisonTableBlockProps) {
  // Guard against empty data
  if (!data.headers.length || !data.rows.length) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {title && (
        <div className="border-b border-gray-200 px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {data.headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left font-medium text-gray-600"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">
                  {row.label}
                </td>
                <td className="px-4 py-2 text-gray-900">{row.current}</td>
                <td className="px-4 py-2 text-gray-500">{row.previous}</td>
                {row.change !== undefined && (
                  <td
                    className={`px-4 py-2 ${
                      row.change > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {row.change > 0 ? '+' : ''}
                    {row.change}%
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Pie Chart (Simple CSS-based representation)
// ============================================================================

interface PieChartBlockProps {
  title?: string;
  data: MilesPieChartData;
}

// Vibrant Excel-like chart color palette
const CHART_COLORS = [
  '#4285F4', // Google Blue
  '#EA4335', // Google Red / Coral
  '#34A853', // Google Green
  '#FBBC04', // Google Yellow / Gold
  '#FF6D01', // Bright Orange
  '#46BDC6', // Teal / Cyan
  '#7B61FF', // Purple / Violet
  '#F538A0', // Magenta / Pink
  '#00ACC1', // Cyan
  '#AB47BC', // Deep Purple
  '#26A69A', // Teal Green
  '#FF7043', // Deep Orange
];

function PieChartBlock({ title, data }: PieChartBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWideEnough, setIsWideEnough] = useState(false);

  // Use ResizeObserver to detect container width and switch layouts
  // This responds to the actual container size, not viewport size
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use horizontal layout only when container is wide enough (>= 450px)
        // to fit both pie chart (224px) and legend comfortably
        setIsWideEnough(entry.contentRect.width >= 450);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const total = data.segments.reduce((sum, seg) => sum + seg.value, 0);

  // Guard against empty data or all-zero values
  if (!data.segments.length || total === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  // Transform data for Recharts PieChart
  const chartData = data.segments.map((segment, index) => ({
    name: segment.label,
    value: segment.value,
    color: segment.color || CHART_COLORS[index % CHART_COLORS.length],
  }));

  return (
    <div
      ref={containerRef}
      className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4"
    >
      {title && (
        <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      )}

      <div
        className={`flex items-center gap-4 ${
          isWideEnough ? 'flex-row' : 'flex-col'
        }`}
      >
        {/* Actual Pie Chart */}
        <div
          className={`flex-shrink-0 ${
            isWideEnough ? 'h-56 w-56' : 'h-48 w-48'
          }`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={isWideEnough ? 40 : 30}
                outerRadius={isWideEnough ? 80 : 65}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                formatter={(value: any, name: any) => [
                  formatValue(value),
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className={`space-y-2 ${isWideEnough ? 'flex-1' : 'w-full'}`}>
          {data.segments.map((segment, index) => {
            const percentage = total > 0 ? (segment.value / total) * 100 : 0;
            const color =
              segment.color || CHART_COLORS[index % CHART_COLORS.length];

            return (
              <div key={index} className="flex min-w-0 items-center gap-3">
                <div
                  className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  <span className="truncate text-sm text-gray-700">
                    {segment.label}
                  </span>
                  <div className="flex-shrink-0 text-right">
                    <span className="text-sm font-medium text-gray-900">
                      {formatValue(segment.value)}
                    </span>
                    <span className="ml-1 text-xs text-gray-500">
                      ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatValue(value: number): string {
  // Format as currency if large number (likely cents)
  if (value >= 100) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value / 100);
  }
  return value.toString();
}

// ============================================================================
// Line Chart
// ============================================================================

interface LineChartBlockProps {
  title?: string;
  data: MilesLineChartData;
}

function LineChartBlock({ title, data }: LineChartBlockProps) {
  // Transform data into recharts format
  // From: { labels: ['Jan', 'Feb'], datasets: [{ label: 'Revenue', data: [100, 200] }] }
  // To: [{ name: 'Jan', Revenue: 100 }, { name: 'Feb', Revenue: 200 }]
  const chartData = data.labels.map((label, index) => {
    const point: Record<string, string | number> = { name: label };
    data.datasets.forEach((dataset) => {
      point[dataset.label] = dataset.data[index] ?? 0;
    });
    return point;
  });

  // Guard against empty data or all-zero values
  const hasData =
    data.labels.length > 0 &&
    data.datasets.length > 0 &&
    data.datasets.some((dataset) => dataset.data.some((value) => value !== 0));

  if (!hasData) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {title && (
        <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      )}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={formatChartValue}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
              formatter={(value: any, name: any) => [
                formatChartValue(value),
                name,
              ]}
              labelStyle={{ fontWeight: 600, marginBottom: 4 }}
            />
            {data.datasets.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
                iconSize={8}
              />
            )}
            {data.datasets.map((dataset, index) => (
              <Line
                key={dataset.label}
                type="monotone"
                dataKey={dataset.label}
                stroke={
                  dataset.color || CHART_COLORS[index % CHART_COLORS.length]
                }
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                activeDot={{ r: 6, strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Bar Chart
// ============================================================================

interface BarChartBlockProps {
  title?: string;
  data: MilesBarChartData;
}

/**
 * Semantic color mapping for financial metrics.
 * Maps dataset labels to appropriate colors based on their meaning.
 */
const SEMANTIC_BAR_COLORS: Record<string, string> = {
  // Revenue-related (green)
  revenue: '#22C55E', // Bright green
  income: '#22C55E',
  earnings: '#22C55E',
  sales: '#22C55E',
  // Profit-related (emerald - slightly different green to distinguish from revenue)
  'net profit': '#10B981', // Emerald
  profit: '#10B981',
  'gross profit': '#10B981',
  'net income': '#10B981',
  margin: '#10B981',
  // Expense-related (red)
  expenses: '#EF4444', // Red
  expense: '#EF4444',
  costs: '#EF4444',
  cost: '#EF4444',
  spending: '#EF4444',
  loss: '#EF4444',
  losses: '#EF4444',
};

/**
 * Gets the appropriate color for a bar chart dataset based on its label.
 * Uses semantic colors for financial metrics, falls back to generic palette.
 */
function getBarColor(
  label: string,
  explicitColor?: string,
  fallbackIndex: number = 0
): string {
  // If dataset has explicit color, use it
  if (explicitColor) return explicitColor;

  // Check for semantic match (case-insensitive)
  const normalizedLabel = label.toLowerCase().trim();
  if (SEMANTIC_BAR_COLORS[normalizedLabel]) {
    return SEMANTIC_BAR_COLORS[normalizedLabel];
  }

  // Check for partial matches (e.g., "Total Revenue" should match "revenue")
  for (const [key, color] of Object.entries(SEMANTIC_BAR_COLORS)) {
    if (normalizedLabel.includes(key)) {
      return color;
    }
  }

  // Fall back to generic chart colors
  return CHART_COLORS[fallbackIndex % CHART_COLORS.length];
}

function BarChartBlock({ title, data }: BarChartBlockProps) {
  // Transform data into recharts format
  // From: { labels: ['Jan', 'Feb'], datasets: [{ label: 'Revenue', data: [100, 200] }] }
  // To: [{ name: 'Jan', Revenue: 100 }, { name: 'Feb', Revenue: 200 }]
  const chartData = data.labels.map((label, index) => {
    const point: Record<string, string | number> = { name: label };
    data.datasets.forEach((dataset) => {
      point[dataset.label] = dataset.data[index] ?? 0;
    });
    return point;
  });

  // Guard against empty data or all-zero values
  const hasData =
    data.labels.length > 0 &&
    data.datasets.length > 0 &&
    data.datasets.some((dataset) => dataset.data.some((value) => value !== 0));

  // Determine if we should use per-bar coloring based on X-axis labels
  // This handles the case where semantic info is in labels (e.g., ['Revenue', 'Expenses', 'Net Profit'])
  // rather than in dataset labels
  const hasSingleDataset = data.datasets.length === 1;
  const labelsHaveSemanticMeaning = data.labels.some((label) => {
    const normalized = label.toLowerCase();
    return Object.keys(SEMANTIC_BAR_COLORS).some((key) =>
      normalized.includes(key)
    );
  });
  const usePerBarColoring = hasSingleDataset && labelsHaveSemanticMeaning;

  // Pre-compute colors for each bar when using per-bar coloring
  const perBarColors = usePerBarColoring
    ? data.labels.map((label, index) => getBarColor(label, undefined, index))
    : [];

  if (!hasData) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {title && (
        <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      )}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#E5E7EB"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={formatChartValue}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
              formatter={(value: any, name: any) => [
                formatChartValue(value),
                name,
              ]}
              labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
            />
            {data.datasets.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
                iconSize={8}
              />
            )}
            {data.datasets.map((dataset, datasetIndex) => (
              <Bar
                key={dataset.label}
                dataKey={dataset.label}
                fill={
                  usePerBarColoring
                    ? undefined
                    : getBarColor(dataset.label, dataset.color, datasetIndex)
                }
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              >
                {/* Use Cell components to color individual bars when X-axis labels have semantic meaning */}
                {usePerBarColoring &&
                  chartData.map((_, barIndex) => (
                    <Cell
                      key={`cell-${barIndex}`}
                      fill={perBarColors[barIndex]}
                    />
                  ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Shared Chart Utilities
// ============================================================================

/**
 * Formats chart axis values and tooltip values.
 * Handles currency (assumes cents), large numbers with K/M suffixes.
 */
function formatChartValue(value: number): string {
  // Handle negative values
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Values are in CENTS, so thresholds need to account for that
  // $1,000,000 = 100,000,000 cents
  // $100,000 = 10,000,000 cents
  // $10,000 = 1,000,000 cents
  // $1,000 = 100,000 cents

  let formatted: string;

  if (absValue >= 100000000) {
    // Millions ($1M+ in cents)
    formatted = `$${(absValue / 100 / 1000000).toFixed(1)}M`;
  } else if (absValue >= 10000000) {
    // Hundred thousands ($100k+ in cents) - show as $XXXk
    formatted = `$${(absValue / 100 / 1000).toFixed(0)}k`;
  } else if (absValue >= 1000000) {
    // Ten thousands ($10k+ in cents) - show as $XX.Xk
    formatted = `$${(absValue / 100 / 1000).toFixed(1)}k`;
  } else if (absValue >= 100000) {
    // Thousands ($1k+ in cents) - show as $X.Xk
    formatted = `$${(absValue / 100 / 1000).toFixed(1)}k`;
  } else if (absValue >= 100) {
    // Regular currency (assumes cents)
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(absValue / 100);
  } else {
    // Small numbers or zero
    formatted = absValue === 0 ? '$0' : `$${(absValue / 100).toFixed(2)}`;
  }

  return isNegative ? `-${formatted}` : formatted;
}

// ============================================================================
// Forecast Chart
// ============================================================================

interface ForecastChartBlockProps {
  title?: string;
  data: MilesForecastChartData;
}

function ForecastChartBlock({ title, data }: ForecastChartBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  // Use ResizeObserver to detect container width and switch layouts
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Switch to compact/vertical layout when container is narrow (collapsed chat)
        setIsCompact(entry.contentRect.width < 450);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Guard against empty data
  if (!data.forecast_points || data.forecast_points.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">
          No forecast data available
        </p>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = data.forecast_points.map((point) => ({
    date: new Date(point.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    balance: point.projected_balance_cents,
    income: point.expected_income_cents,
    expenses: point.expected_expenses_cents,
  }));

  // Determine risk color
  const riskColors = {
    low: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
    },
    medium: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
    },
    high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  };
  const risk = riskColors[data.risk_level] || riskColors.low;

  const balanceChange =
    data.projected_end_balance_cents - data.starting_balance_cents;
  const isPositive = balanceChange >= 0;

  return (
    <div
      ref={containerRef}
      className="rounded-xl border border-gray-200 bg-white p-4"
    >
      {title && (
        <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      )}

      {/* Summary Cards - Stack vertically in compact view */}
      <div
        className={`mb-4 grid gap-2 ${isCompact ? 'grid-cols-1' : 'grid-cols-2 gap-3 lg:grid-cols-4'}`}
      >
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
          <p className="text-xs text-gray-500">Starting Balance</p>
          <p className="text-base font-semibold text-gray-900">
            {formatChartValue(data.starting_balance_cents)}
          </p>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
          <p className="text-xs text-gray-500">Projected Balance</p>
          <p className="text-base font-semibold text-gray-900">
            {formatChartValue(data.projected_end_balance_cents)}
          </p>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
          <p className="text-xs text-gray-500">Net Change</p>
          <p
            className={`text-base font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}
          >
            {isPositive ? '+' : ''}
            {formatChartValue(balanceChange)}
          </p>
        </div>
        <div
          className={`flex items-center justify-between rounded-lg p-3 ${risk.bg} ${risk.border} border`}
        >
          <p className="text-xs text-gray-500">Risk Level</p>
          <p className={`text-base font-semibold capitalize ${risk.text}`}>
            {data.risk_level}
          </p>
        </div>
      </div>

      {/* Forecast Chart */}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#E5E7EB"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={formatChartValue}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
              formatter={(value: any, name: any) => [
                formatChartValue(value),
                name === 'Balance'
                  ? 'Projected Balance'
                  : name === 'Income'
                    ? 'Expected Income'
                    : 'Expected Expenses',
              ]}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Line
              type="monotone"
              dataKey="balance"
              name="Balance"
              stroke="#4285F4"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Risk Factors */}
      {data.risk_factors.length > 0 && (
        <div className={`mt-4 rounded-lg p-3 ${risk.bg}`}>
          <div className="mb-2 flex items-center gap-2">
            <ShieldWarning size={16} className={risk.text} weight="bold" />
            <p className={`text-sm font-medium ${risk.text}`}>Risk Factors</p>
          </div>
          <ul className="space-y-1">
            {data.risk_factors.map((factor, index) => (
              <li key={index} className={`text-sm ${risk.text}`}>
                • {factor}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recurring Transactions */}
      {data.recurring_transactions.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-gray-700">
            Expected Recurring Transactions
          </p>
          <div className="space-y-2">
            {data.recurring_transactions.slice(0, 5).map((txn, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  {txn.type === 'income' ? (
                    <TrendUpIcon
                      size={16}
                      className="text-emerald-500"
                      weight="bold"
                    />
                  ) : (
                    <TrendDownIcon
                      size={16}
                      className="text-red-500"
                      weight="bold"
                    />
                  )}
                  <span className="text-sm text-gray-700">
                    {txn.merchant_name}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({txn.frequency})
                  </span>
                </div>
                <div className="text-right">
                  <span
                    className={`text-sm font-medium ${txn.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}
                  >
                    {txn.type === 'income' ? '+' : '-'}
                    {formatChartValue(txn.average_amount_cents)}
                  </span>
                  <p className="text-xs text-gray-400">
                    Next:{' '}
                    {new Date(txn.next_expected_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Insights List
// ============================================================================

interface InsightsListBlockProps {
  title?: string;
  data: MilesInsightsListData;
}

function InsightsListBlock({ title, data }: InsightsListBlockProps) {
  // Guard against empty data
  if (
    (!data.insights || data.insights.length === 0) &&
    (!data.top_categories || data.top_categories.length === 0)
  ) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {title && (
          <h4 className="mb-3 text-sm font-semibold text-gray-900">{title}</h4>
        )}
        <p className="text-center text-sm text-gray-500">
          No insights available
        </p>
      </div>
    );
  }

  // Severity styling
  const severityStyles = {
    high: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: 'text-red-500',
    },
    medium: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: 'text-amber-500',
    },
    low: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-500',
    },
    info: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-700',
      icon: 'text-gray-500',
    },
  };

  // Type icons
  const typeIcons = {
    anomaly: ShieldWarning,
    trend: ChartLine,
    optimization: Lightbulb,
    warning: Warning,
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {title && (
        <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      )}

      {/* Summary Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg bg-gray-50 p-3">
        <div>
          <p className="text-xs text-gray-500">Total Insights</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.summary.total_insights}
          </p>
        </div>
        {data.summary.high_priority_count > 0 && (
          <div>
            <p className="text-xs text-gray-500">High Priority</p>
            <p className="text-lg font-semibold text-red-600">
              {data.summary.high_priority_count}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-500">Period</p>
          <p className="text-lg font-semibold text-gray-900">
            {data.summary.period_days} days
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Expenses</p>
          <p className="text-lg font-semibold text-gray-900">
            {formatChartValue(data.summary.total_expenses_cents)}
          </p>
        </div>
      </div>

      {/* Insights List */}
      {data.insights.length > 0 && (
        <div className="mb-4 space-y-3">
          {data.insights.map((insight, index) => {
            const style =
              severityStyles[insight.severity] || severityStyles.info;
            const IconComponent = typeIcons[insight.type] || Lightbulb;

            return (
              <div
                key={insight.insight_id || index}
                className={`rounded-lg border p-3 ${style.bg} ${style.border}`}
              >
                <div className="flex items-start gap-3">
                  <IconComponent
                    size={20}
                    className={`mt-0.5 flex-shrink-0 ${style.icon}`}
                    weight="bold"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h5 className={`text-sm font-semibold ${style.text}`}>
                        {insight.title}
                      </h5>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${style.bg} ${style.text} border ${style.border}`}
                      >
                        {insight.type}
                      </span>
                      {insight.category && (
                        <span className="text-xs text-gray-500">
                          {insight.category}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {insight.description}
                    </p>
                    {insight.recommended_action && (
                      <div className="mt-2 flex items-center gap-1 text-sm text-gray-700">
                        <ArrowRight size={14} className="text-gray-400" />
                        <span className="font-medium">Action:</span>
                        <span>{insight.recommended_action}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Top Categories */}
      {data.top_categories.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">
            Top Spending Categories
          </p>
          <div className="space-y-2">
            {data.top_categories.map((category, index) => {
              const trendIcon =
                category.trend === 'increasing' ? (
                  <TrendUpIcon
                    size={14}
                    className="text-red-500"
                    weight="bold"
                  />
                ) : category.trend === 'decreasing' ? (
                  <TrendDownIcon
                    size={14}
                    className="text-emerald-500"
                    weight="bold"
                  />
                ) : (
                  <span className="h-0.5 w-3.5 rounded bg-gray-400" />
                );

              return (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor:
                          CHART_COLORS[index % CHART_COLORS.length],
                      }}
                    />
                    <span className="text-sm text-gray-700">
                      {category.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">
                      {formatChartValue(category.total_cents)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({category.percentage_of_total}%)
                    </span>
                    {trendIcon}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default MilesStructuredData;
